/**
 * Reversed-voucher POs ("RV" on the third-party tabs).
 *
 * A vendor invoice gets vouchered against a PO and then reversed (or credited
 * back), so the PO's cost history nets to $0 and nothing is left open. The tabs
 * sum cost per row, so +315 / −315 collapses to a blank line and the cost looks
 * like it never existed — Josh Daley's PO 227674 on 12426010 (8/21/2026): twelve
 * OV rows on 6/23 in offsetting pairs, never received, open $0.
 *
 * Judged per PO across the whole job, not per line: AP often reverses a voucher
 * off one line and re-matches it to another line of the same PO. Those lines net
 * to zero too, but the PO still carries the cost, so they are not flagged.
 */

// JDE voucher-family document types on cost history: OV voucher match against
// a receipt, PV standard voucher, PD debit memo (vendor credit).
const VOUCHER_DOCS = new Set(['OV', 'PV', 'PD']);

const cents = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

// '00227674' / 227674 / '227674' → '227674'; '' for rows with no real PO.
export const poKey = (po) => {
    const n = parseInt(String(po ?? '').trim(), 10);
    return Number.isFinite(n) && n > 0 ? String(n) : '';
};

// Line ids arrive as 1, '1' or '1.000' depending on the feed.
export const lineKey = (line) => {
    const n = parseFloat(String(line ?? '').trim());
    return Number.isFinite(n) ? String(n) : '';
};

const dayOf = (d) => (d ? String(d).slice(0, 10) : '');

/**
 * Find the job's reversed-voucher POs.
 *
 * @param {Array} costRows – cost history (Job_Cost, Document_Type, PO_Number, Line_ID, GL_Date)
 * @param {Array} ocRows   – open commitments (PO_Number, PO_True_Open_Amount)
 * @returns {Map} poKey → { po, vendor, posted, last, lines: Map(lineKey → { posted, last }) }
 *          posted is the gross vouchered before the reversal, in dollars.
 */
export const findReversedPos = (costRows, ocRows) => {
    const byPo = new Map();
    (costRows || []).forEach((r) => {
        const key = poKey(r?.PO_Number);
        if (!key) return;
        let e = byPo.get(key);
        if (!e) {
            e = { po: key, vendor: r.Supplier_Name || '', net: 0, open: 0, posted: 0, backedOut: 0, last: '', lines: new Map() };
            byPo.set(key, e);
        }
        const amt = cents(r.Job_Cost);
        e.net += amt;
        if (!VOUCHER_DOCS.has(String(r.Document_Type || '').trim())) return;
        const day = dayOf(r.GL_Date);
        if (amt > 0) e.posted += amt;
        else e.backedOut += amt;
        if (day > e.last) e.last = day;
        const lk = lineKey(r.Line_ID);
        const ln = e.lines.get(lk) || { posted: 0, last: '' };
        if (amt > 0) ln.posted += amt;
        if (day > ln.last) ln.last = day;
        e.lines.set(lk, ln);
    });
    (ocRows || []).forEach((r) => {
        const e = byPo.get(poKey(r?.PO_Number));
        if (e) e.open += cents(r.PO_True_Open_Amount);
    });

    const flagged = new Map();
    byPo.forEach((e, key) => {
        if (e.posted <= 0 || e.backedOut >= 0 || e.net !== 0 || e.open !== 0) return;
        const lines = new Map();
        e.lines.forEach((ln, lk) => lines.set(lk, { posted: ln.posted / 100, last: ln.last }));
        flagged.set(key, { po: key, vendor: e.vendor, posted: e.posted / 100, last: e.last, lines });
    });
    return flagged;
};

/**
 * The reversed POs a tree node covers. A PO row reports its own PO; a Line_ID
 * row reports its PO only when that line carried the reversed voucher; any
 * grouping row above (phase, vendor, cost type, resource) reports every flagged
 * PO beneath it. Leaf transaction rows report nothing.
 *
 * @returns {Array} flagged entries (see findReversedPos), [] when none
 */
export const reversedUnder = (node, flagged) => {
    if (!node?.data || !flagged?.size) return [];
    const { path, key, detail } = node.data;
    if (path === 'PO_Number') {
        const e = flagged.get(poKey(key));
        return e ? [e] : [];
    }
    if (path === 'Line_ID') {
        const e = flagged.get(poKey(detail?.PO_Number));
        return e && e.lines.has(lineKey(key)) ? [e] : [];
    }
    if (!node.rows) return [];
    const found = new Map();
    const walk = (n) => {
        Object.values(n.rows || {}).forEach((child) => {
            const e = flagged.get(poKey(child?.data?.detail?.PO_Number));
            if (e) found.set(e.po, e);
            if (child?.rows) walk(child);
        });
    };
    walk(node);
    return [...found.values()];
};

/** Every flagged PO that has a row anywhere in a tab's tree. */
export const reversedInTree = (tree, flagged) => {
    if (!flagged?.size) return [];
    const found = new Map();
    Object.values(tree || {}).forEach((top) => {
        reversedUnder(top, flagged).forEach((e) => found.set(e.po, e));
    });
    return [...found.values()].sort((a, b) => b.posted - a.posted);
};
